import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { default as timeZoneIds } from '../../../i18n/timeZones';

import {
  Checkbox,
  Combobox,
  Input,
  Timepicker
} from '@salesforce/design-system-react';

import './ImportSchedule.scss';

const DEFAULT_TIME = '12:00 AM';
const DEFAULT_DAYS = 14;
const MIN_DAYS = 1;
const MAX_DAYS = 30;
// TODO: we can use moment-timezone guess() but we'd have to change these to America/Chicago style identifiers
// including in metalMastodon
const DEFAULT_TIME_ZONE_ID = 'Eastern Time (US & Canada)';

// converts "HH:MM AM" string to an object { hour: HH, minute: MM, am_pm: "AM" }
const parseHoursAndMinutes = str => {
  const parts = str.split(':');
  const hour = parseInt(parts[0]);
  const minute = parseInt(parts[1].substring(0, 2));
  const am_pm = parts[1].substr(-2);
  return { hour, minute, am_pm };
};

const parseDate = date => {
  const minute = date.getMinutes();
  let hour = date.getHours();
  const am_pm = hour >= 12 ? 'PM' : 'AM';
  if (am_pm === 'PM') {
    hour = hour - 12;
  }

  return { hour, minute, am_pm };
};

class ImportSchedule extends Component {
  handleScheduleChange(field, data) {
    switch (field) {
      case 'importRecurring':
        const update = {
          importRecurring: data
        };
        // When we toggle on a recurring import, update the form with all of the defaults so we don't need to reference
        // those defaults elsewhere
        if (data) {
          if (!this.props.schedule?.sync_days) {
            update.sync_days = this.defaults('sync_days');
          }
          if (!this.props.schedule?.hour) {
            const defaultTime = parseHoursAndMinutes(
              this.defaults('importTime')
            );
            update.hour = defaultTime.hour;
            update.minute = defaultTime.minute;
            update.am_pm = defaultTime.am_pm;
          }
          if (!this.props.schedule?.time_zone) {
            update.time_zone = this.defaults('time_zone')[0].id;
          }
        }
        this.props.formChange(update);
        break;
      case 'sync_days':
        this.props.formChange({ [field]: parseInt(data) });
        break;
      case 'importTime':
        const parsed = parseDate(data.dateValue);
        this.props.formChange(parsed);
        break;
      case 'time_zone':
        this.props.formChange({ [field]: data?.[0]?.id });
        break;
      default:
        break;
    }
  }

  defaults(field) {
    switch (field) {
      case 'importRecurring':
        return false;
      case 'sync_days':
        return DEFAULT_DAYS;
      case 'importTime':
        return DEFAULT_TIME;
      case 'time_zone':
        return [this.timeZoneFor(DEFAULT_TIME_ZONE_ID)];
      default:
    }
  }

  /**
   * Gets the current value that should go into an input control for a given field
   */
  selectedSchedule(field) {
    switch (field) {
      // Timezone is a combobox, the selection should be an array
      case 'time_zone':
        return this.props.schedule?.time_zone
          ? [this.timeZoneFor(this.props.schedule.time_zone)]
          : this.defaults(field);
      // Import time is stored in the schedule as hour, minute, am_pm but we just need the combined value
      case 'importTime':
        const schedule = this.props.schedule || {};
        const { hour, minute, am_pm } = schedule;
        if (hour && minute >= 0 && am_pm) {
          // add leading 0's to match what the Timepicker shows in the dropdown
          const minutes = minute < 10 ? `0${minute}` : minute;
          const hours = hour < 10 ? `0${hour}` : hour;
          return `${hours}:${minutes} ${am_pm}`;
        } else {
          return this.defaults(field);
        }
      default:
        return this.props.schedule?.[field] || this.defaults(field);
    }
  }

  renderScheduleControls() {
    const { formatMessage } = this.props.intl;
    // Don't show the scheduled controls if this is not a recurring import
    if (!this.props.schedule?.importRecurring) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="import-sync-period">
          <div className="import-period-input">
            <Input
              label={formatMessage({
                id: 'components.ecs.import.setPeriod.label'
              })}
              value={this.selectedSchedule('sync_days')}
              defaultValue={DEFAULT_DAYS}
              minValue={MIN_DAYS}
              maxValue={MAX_DAYS}
              variant="counter"
              onChange={(event, { value }) =>
                this.handleScheduleChange('sync_days', value)
              }
            />
            <div className="import-period-units-label">
              <FormattedMessage id="components.ecs.import.setPeriod.days" />
            </div>
          </div>
          <div className="import-select-label">
            <FormattedMessage id="components.ecs.import.setPeriod.range" />
          </div>
        </div>
        <div className="import-sync-time">
          <Timepicker
            label={formatMessage({ id: 'components.ecs.import.setTime' })}
            placeholder={formatMessage({
              id: 'components.ecs.import.pickTime'
            })}
            stepInMinutes={30}
            strValue={this.selectedSchedule('importTime')}
            onDateChange={(dateValue, stringValue) =>
              this.handleScheduleChange('importTime', {
                dateValue,
                stringValue
              })
            }
            variant="readonly"
          />
        </div>
        <div className="import-time-zone">
          <Combobox
            labels={{
              label: formatMessage({
                id: 'components.ecs.import.setTimeZone.label'
              }),
              placeholderReadOnly: formatMessage({
                id: 'components.ecs.import.timezone'
              })
            }}
            readonly
            options={this.timeZones()}
            selection={this.selectedSchedule('time_zone')}
            events={{
              onSelect: (evt, { selection }) =>
                this.handleScheduleChange('time_zone', selection)
            }}
            variant="readonly"
          />
          <div className="import-select-label">
            <FormattedMessage id="components.ecs.import.setTimeZone.dst" />
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;

    return (
      <div className="import-schedule">
        <h3 className="import-label">
          <FormattedMessage id="components.ecs.import.schedule.label" />
        </h3>
        <Checkbox
          className="import-recurring"
          labels={{
            label: formatMessage({
              id: 'components.ecs.import.recurring'
            })
          }}
          onChange={(evt, { checked }) =>
            this.handleScheduleChange('importRecurring', checked)
          }
          checked={this.selectedSchedule('importRecurring')}
        />
        {this.renderScheduleControls()}
      </div>
    );
  }

  timeZones() {
    return timeZoneIds.map(t => this.timeZoneFor(t));
  }

  timeZoneFor(id) {
    const { formatMessage } = this.props.intl;
    return {
      id,
      label: formatMessage({ id: `timeZones.${id}` })
    };
  }
}

ImportSchedule.propTypes = {
  formChange: PropTypes.func,
  location: PropTypes.object,
  schedule: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ImportSchedule);
